import {
useCallback,
useState
} from "react"
import { useTranslation } from "react-i18next";
import {
  ListSettingsLineIcon,
  LogoutBoxLineIcon,
  SettingsSixLineIcon,
  ShieldKeyholeLineIcon,
  ShieldUserLineIcon,
} from "@ifrc-go/icons";
import {
  Button,
  DropdownMenu,
  PageContainer
} from "@ifrc-go/ui"
import { _cs } from "@togglecorp/fujs";

import LanguageSelect from "#components/LanguageSelect";
import Link from '#components/Link';
import NavLink from "#components/NavLink";
import useAlert from "#hooks/useAlert";
import { logout } from "#redux/actions/authActions";
import {
  useAppDispatch,
  useAppSelector
} from "#redux/store"
import { AuthAPI } from "#utils";

import styles from "./styles.module.css";

interface Props {
  className?: string;
}

function Navbar(props: Props) {
  const { className } = props;

  const { t } = useTranslation();
  const auth = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const alert = useAlert();

  const [logoutPending, setLogoutPending] = useState(false);

  const handleLogout = useCallback(
    () => {
      setLogoutPending(true);
      AuthAPI.delete('/browser/v1/auth/session?client=browser')
        .then(() => {
          dispatch(logout);
          setLogoutPending(false);
        })
        .catch((ex) => {
          if (ex?.response?.data?.meta?.is_authenticated === false) {
            dispatch(logout());
          } else {
            alert.show(
             t("nav.failedToLogout"),
              { variant: 'danger' },
            );
          }
          setLogoutPending(false);
        });
    },
    [alert, dispatch,t],
  );

  return (
    <nav className={_cs(styles.navbar, className)}>
      <PageContainer
        contentClassName={styles.topContent}
        className={styles.top}
      >
        <div className={styles.brand}>
          <Link
            to="/"
            className={styles.titleLink}
          >
            {`${t("landingPage.title")}`}
          </Link>
          {!["production", "prod"].includes(import.meta.env.APP_ENVIRONMENT.toLowerCase()) && (
            <div className={styles.env}>
              {import.meta.env.APP_ENVIRONMENT}
            </div>
          )}
        </div>
        <div className={styles.actions}>
          <LanguageSelect />
          {auth.is_logged && (
            <DropdownMenu
              label={auth.user?.display_name}
              icons={<ShieldUserLineIcon />}
              variant="tertiary"
            >
              {auth.user?.can_access_cms && (
                <Link
                  external
                  href={`${import.meta.env.APP_API_ENDPOINT}/admin/`}
                  icons={<ListSettingsLineIcon />}
                  variant="dropdown-item"
                  withLinkIcon
                >
                  {auth.user?.read_only_member
                    ? t("nav.codebook")
                    : t("nav.cms")}
                </Link>
              )}
              {auth.user?.can_access_cms && (
                <Link
                  external
                  href={`${import.meta.env.APP_API_ENDPOINT}/api/docs/`}
                  icons={<SettingsSixLineIcon />}
                  variant="dropdown-item"
                  withLinkIcon
                >
                  {t("nav.apiDocs")}
                </Link>
              )}
              <Link
                to="/api-keys/"
                variant="dropdown-item"
                icons={<ShieldKeyholeLineIcon />}
              >
                {t("nav.manageApiKeys")}
              </Link>
              <form
                action={`${import.meta.env.APP_API_ENDPOINT}/_allauth/`}
                method="DELETE"
              >
                <input
                  type="hidden"
                  name="client"
                  value="browser"
                />
                <Button
                  name={undefined}
                  onClick={handleLogout}
                  variant="dropdown-item"
                  disabled={logoutPending}
                  icons={<LogoutBoxLineIcon />}
                >
                  {t("actions.logout")}
                </Button>
              </form>
            </DropdownMenu>
          )}
        </div>
      </PageContainer>
      <PageContainer contentClassName={styles.bottomContent}>
        <NavLink
          to={auth.is_logged ? "/design/survey/" : "/"}
          exact
          className={styles.titleLink}
        >
          {auth.is_logged ? t("nav.isLoggedSurveyWizard") : t("nav.isLoggedHome")}
        </NavLink>
        {!auth.is_logged && (
          <NavLink
            to="/login/"
          >
            {t("actions.login")}
          </NavLink>
        )}
        {auth.is_logged && (
          <NavLink
            to="/surveys/"
            className={styles.titleLink}
          >
            {t("nav.savedSurveys")}
          </NavLink>
        )}
        <NavLink
            to="/help/"
        >
          {t("actions.help")}
        </NavLink>
      </PageContainer>
    </nav>
  );
}

export default Navbar;
